import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import useAuth from '../hooks/useAuth';
import { Button, Card } from 'react-bootstrap';
import '../App.css';

const Billing = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth, setAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');

  const checkoutResult = searchParams.get('checkout');

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get('/billing/status');
      setStatus(res.data);
      setAuth((prev) => ({
        ...prev,
        plan: res.data.plan,
        subscriptionStatus: res.data.subscriptionStatus,
        limits: res.data.limits,
      }));
    } catch (err) {
      setMessage('Could not load billing status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (checkoutResult === 'success') setMessage('Thanks! Your subscription should activate shortly.');
    if (checkoutResult === 'cancel') setMessage('Checkout canceled — no charges made.');
  }, [checkoutResult]);

  const startCheckout = async () => {
    setActionLoading(true);
    setMessage('');
    try {
      const res = await axiosPrivate.post('/billing/checkout');
      if (res.data?.url) window.location.href = res.data.url;
      else setMessage('Billing is not configured yet.');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not start checkout.');
    } finally {
      setActionLoading(false);
    }
  };

  const openPortal = async () => {
    setActionLoading(true);
    setMessage('');
    try {
      const res = await axiosPrivate.post('/billing/portal');
      if (res.data?.url) window.location.href = res.data.url;
      else setMessage('Billing portal unavailable.');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not open billing portal.');
    } finally {
      setActionLoading(false);
    }
  };

  const isPro = status?.plan === 'pro' && ['active', 'trialing'].includes(status?.subscriptionStatus);

  return (
    <div className="recipe-lab-page" style={{ maxWidth: 720, margin: '2rem auto', padding: '0 1rem' }}>
      <h1>Recipe Lab Plans</h1>
      <p className="text-muted">
        Community recipes, the map, and grocery store tools are always free.
        Pro unlocks more private recipe slots.
      </p>

      {message && <div className="alert alert-info">{message}</div>}

      {loading ? (
        <p>Loading plan details…</p>
      ) : (
        <>
          <Card className="mb-3">
            <Card.Body>
              <Card.Title>Your plan</Card.Title>
              <Card.Text>
                <strong>{isPro ? 'Pro' : 'Free'}</strong>
                {status?.subscriptionStatus && status.subscriptionStatus !== 'inactive' && (
                  <> · {status.subscriptionStatus}</>
                )}
              </Card.Text>
              {auth?.user && <Card.Text className="text-muted">Signed in as {auth.user}</Card.Text>}
            </Card.Body>
          </Card>

          <div className="row g-3">
            <div className="col-md-6">
              <Card>
                <Card.Body>
                  <Card.Title>Free — always</Card.Title>
                  <Card.Text>
                    <ul>
                      <li>5 private recipes in My Recipes</li>
                      <li>Community book — browse, add, and publish</li>
                      <li>Build pages with images</li>
                      <li>Restaurant map and grocery price tools</li>
                      <li>Favorites</li>
                    </ul>
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-6">
              <Card border="primary">
                <Card.Body>
                  <Card.Title>Pro</Card.Title>
                  <Card.Text>
                    <ul>
                      <li>Unlimited private recipes</li>
                      <li>Everything in Free</li>
                    </ul>
                  </Card.Text>
                  {!isPro ? (
                    <Button variant="primary" onClick={startCheckout} disabled={actionLoading || !status?.stripeConfigured}>
                      {status?.stripeConfigured ? 'Upgrade to Pro' : 'Coming soon'}
                    </Button>
                  ) : (
                    <Button variant="outline-primary" onClick={openPortal} disabled={actionLoading}>
                      Manage subscription
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>

          <p className="mt-4">
            <Link to="/recipes">← Back to recipes</Link>
          </p>
        </>
      )}
    </div>
  );
};

export default Billing;